import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

/**
 * UI-08 — Jenna: "Unable to resize the side window. This really affects my
 * ability to review what is parsed from the document."
 *
 * Drag state for a panel docked on the RIGHT of a flex row. The panel keeps
 * the existing layout; wrapping both panes in react-resizable-panels would
 * have restructured a container that also carries fullscreen mode and an
 * outline sidebar, for no extra behaviour.
 */

const KEYBOARD_STEP = 16
const KEYBOARD_STEP_LARGE = 64

export interface ResizablePanel {
  width: number
  isDragging: boolean
  /** The live upper bound: `max`, reduced so the sibling pane stays usable. */
  effectiveMax: number
  /** Spread onto the drag handle — behaviour and a11y semantics together. */
  handleProps: {
    role: 'separator'
    tabIndex: 0
    'aria-orientation': 'vertical'
    'aria-valuenow': number
    'aria-valuemin': number
    'aria-valuemax': number
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  }
}

export function useResizablePanel({
  defaultWidth,
  defaultFraction,
  min,
  max,
  storageKey,
  containerRef,
  minComplementWidth,
}: {
  /** Fallback opening width, used until the row can be measured. */
  defaultWidth: number
  /**
   * Opening width as a share of the row, once it can be measured.
   *
   * UI-55 (Putra): "fill the space, why not make this bigger when user open".
   * A fixed opening width cannot answer that — 420px is a fair panel on a
   * laptop and a narrow strip beside a mostly-empty document pane on a 1920px
   * monitor. Applied ONCE, when the row is first measured, and never over a
   * width the reader has already chosen.
   */
  defaultFraction?: number
  min: number
  max: number
  storageKey: string
  /** The flex row holding this panel and the pane it shares space with. */
  containerRef: React.RefObject<HTMLElement | null>
  /** Width the sibling pane must keep, so it can never be squeezed away. */
  minComplementWidth: number
}): ResizablePanel {
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  /** Did the reader already have a width? Set once, in the initialiser below. */
  const hadStoredWidth = useRef(false)
  const [width, setWidth] = useState(() => {
    try {
      const stored = Number(window.localStorage.getItem(storageKey))
      if (Number.isFinite(stored) && stored >= min && stored <= max) {
        hadStoredWidth.current = true
        return stored
      }
    } catch {
      // private windows throw rather than returning null
    }
    return defaultWidth
  })

  // A static bound cannot know the viewport (greptile P1): a persisted 900px on
  // a 1024px window left the document ~44px, and less once the 220px outline
  // opened, because this panel does not shrink while the document pane does.
  // The ceiling is whatever leaves the document its minimum.
  const effectiveMax = useMemo(() => {
    if (containerWidth === null) return max
    return Math.max(min, Math.min(max, containerWidth - minComplementWidth))
  }, [containerWidth, max, min, minComplementWidth])

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setContainerWidth(el.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [containerRef])

  // Re-clamp when the room changes — shrinking the window, or opening the
  // outline — so a width that was legal before cannot strand the document.
  useEffect(() => {
    setWidth((current) => Math.min(effectiveMax, Math.max(min, current)))
  }, [effectiveMax, min])

  /**
   * The width this panel opens at: the fraction of the measured row when one is
   * given, else the fixed fallback. Clamped like any other width, so a wide
   * monitor cannot push it past `max` or starve the document pane.
   */
  const resolvedDefault = useMemo(() => {
    if (containerWidth === null || defaultFraction === undefined) return defaultWidth
    const proportional = Math.round(containerWidth * defaultFraction)
    return Math.min(effectiveMax, Math.max(min, proportional))
  }, [containerWidth, defaultFraction, defaultWidth, effectiveMax, min])

  // Adopt it once, when the row is first measured. Guarded three ways so this
  // can only ever affect the opening width: not if the reader has a stored
  // width, not if they are mid-drag, and never a second time.
  const appliedDefault = useRef(false)
  /**
   * Set by the drag and keyboard paths — the only two ways a reader changes the
   * width. Persistence keys off this rather than comparing against the default:
   * in the commit where the row is first measured the effect would otherwise
   * see the pre-adoption width against the post-measurement default and store
   * an untouched panel as though someone had chosen it.
   */
  const userChanged = useRef(false)
  useEffect(() => {
    if (appliedDefault.current || containerWidth === null) return
    appliedDefault.current = true
    if (hadStoredWidth.current || defaultFraction === undefined) return
    setWidth(resolvedDefault)
  }, [containerWidth, defaultFraction, resolvedDefault])

  const clamp = useCallback(
    (value: number) => Math.min(effectiveMax, Math.max(min, value)),
    [effectiveMax, min],
  )

  // The pointer that started the drag. Without it a second finger could move
  // or end someone else's drag, and a release outside the window would leave
  // `isDragging` stuck until an unrelated pointer event arrived (greptile P2).
  const dragRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null)

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (dragRef.current) return
      event.preventDefault()
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: width,
      }
      // Capture routes every later event for this pointer to the handle, so
      // leaving the 4px strip — or the window — cannot drop the gesture.
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        // jsdom and older browsers: the window listeners below still work.
      }
      setIsDragging(true)
    },
    [width],
  )

  useEffect(() => {
    if (!isDragging) return

    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      // Panel is on the RIGHT: dragging left (negative delta) widens it.
      userChanged.current = true
      setWidth(clamp(drag.startWidth - (event.clientX - drag.startX)))
    }
    const end = (event?: PointerEvent) => {
      if (event && dragRef.current && event.pointerId !== dragRef.current.pointerId) return
      dragRef.current = null
      setIsDragging(false)
    }

    // Named, not an inline arrow: add and remove must be the SAME reference or
    // the listener is never removed.
    const onBlur = () => end()

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    window.addEventListener('lostpointercapture', end)
    // Releasing outside the browser never delivers pointerup; blur is the only
    // signal that the gesture is over.
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      window.removeEventListener('lostpointercapture', end)
      window.removeEventListener('blur', onBlur)
    }
  }, [isDragging, clamp])

  // A pointer-only separator is unusable without a mouse (greptile P1).
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP
      let next: number | null = null
      // Left widens, matching the drag direction for a right-docked panel.
      if (event.key === 'ArrowLeft') next = width + step
      else if (event.key === 'ArrowRight') next = width - step
      else if (event.key === 'Home') next = min
      else if (event.key === 'End') next = effectiveMax
      if (next === null) return
      event.preventDefault()
      userChanged.current = true
      setWidth(clamp(next))
    },
    [width, min, effectiveMax, clamp],
  )

  useEffect(() => {
    if (isDragging) return
    // Don't write on mount: storing `defaultWidth` before anyone has touched
    // the panel makes an untouched default indistinguishable from a choice.
    // Don't write on mount, or for the opening width: storing a width nobody
    // chose makes an untouched default indistinguishable from a decision, and
    // would freeze the panel at whatever monitor it was first opened on.
    if (!userChanged.current) return
    try {
      window.localStorage.setItem(storageKey, String(width))
    } catch {
      // remembering a width must never be what breaks the panel
    }
  }, [isDragging, width, storageKey])

  return {
    width,
    isDragging,
    effectiveMax,
    handleProps: {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'vertical',
      'aria-valuenow': Math.round(width),
      'aria-valuemin': min,
      'aria-valuemax': Math.round(effectiveMax),
      onPointerDown,
      onKeyDown,
    },
  }
}
